import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Sparkles, Star } from "lucide-react";
import { KineticLines, EASE } from "@/components/common/Reveal";
import { CtaPair } from "@/components/common/CtaButtons";
import { IMAGES } from "@/data/site";

const floatCards = [
  { icon: ShieldCheck, label: "Experienced Dentist", pos: "left-4 top-8" },
  { icon: Sparkles, label: "Advanced Technology", pos: "bottom-8 right-4" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-brand-accent text-white pt-32 md:pt-40 lg:pt-44"
    >
      {/* Ambient background accents */}
      <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-brand-blue/25 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-brand-green/15 blur-[130px]" />

      <div className="relative mx-auto grid max-w-[1440px] items-center gap-12 px-6 pb-20 md:px-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-20 lg:pb-28">
        {/* Left */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="hero-doctor-name"
            className="font-display text-lg font-semibold tracking-wide text-white/90 md:text-xl"
          >
            Dr. Ankesh Kr. Seth
          </motion.p>

          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-brand-green backdrop-blur-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            Best Dentist in Paharia, Varanasi
          </motion.span>

          <h1 className="mt-6 font-display text-5xl font-semibold leading-[0.95] tracking-tight text-white md:text-7xl lg:text-[5.4rem]">
            <KineticLines
              lines={["Advanced,", "Painless"]}
              delay={0.15}
            />
            <span className="reveal-mask">
              <motion.span
                className="block italic text-brand-green"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, ease: EASE, delay: 0.39 }}
              >
                Dental Care.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
          >
            From gentle cleanings to advanced implants — Dr. Ankesh Seth brings modern technology,
            strict sterilization and a calm, patient-first experience to every smile in Varanasi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-8"
          >
            <CtaPair />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 1 }}
            className="mt-10 flex items-center gap-6"
          >
            <div className="flex -space-x-3">
              {[IMAGES.patient, IMAGES.dentist, IMAGES.clinic2].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt="Happy patient"
                  className="h-11 w-11 rounded-full border-2 border-brand-accent object-cover"
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-brand-green">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="text-sm font-medium text-white/60">Loved by 5,000+ Varanasi patients</p>
            </div>
          </motion.div>
        </div>

        {/* Right — parallax image with floating cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.3 }}
          className="relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-white/5 ring-1 ring-white/10">
            <motion.img
              src={IMAGES.doctor}
              alt="Dr. Ankesh Kr. Seth"
              style={{ y, scale }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-accent/60 via-brand-accent/0 to-transparent" />
          </div>

          {floatCards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 + i * 0.15 }}
              className={`absolute ${c.pos} flex items-center gap-2.5 rounded-2xl border border-white/15 bg-white/90 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)] backdrop-blur-md`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                <c.icon className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold text-brand-dark">{c.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
